/**
 * API key service calls.
 *
 * This module provides functions for personal API key management: listing the
 * keys an account holds, creating a new one, and revoking an existing one. The
 * raw key is returned by the server only at creation time and is never
 * retrievable afterwards.
 */

import apiClient from './api';

/**
 * Get all API keys for the current user
 * @returns {Promise<Array>} Key metadata; never includes the raw key
 */
export const getApiKeys = async () => {
  try {
    const response = await apiClient.get('/api/api-keys');
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to fetch API keys'
    );
  }
};

/**
 * Create an API key
 * @param {string} name - Human label for the key
 * @param {string} scope - 'general' | 'collector' | 'wechat_feed'
 * @returns {Promise<Object>} Created key, including the raw `key` shown once
 */
export const createApiKey = async (name, scope) => {
  try {
    const response = await apiClient.post('/api/api-keys', { name, scope });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to create API key'
    );
  }
};

/**
 * Revoke an API key
 * @param {number} keyId - Key id to revoke
 * @returns {Promise<void>}
 */
export const revokeApiKey = async (keyId) => {
  try {
    await apiClient.delete(`/api/api-keys/${keyId}`);
  } catch (error) {
    throw new Error(
      error.response?.data?.detail || 'Failed to revoke API key'
    );
  }
};
